import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../../loaders';
import { Button, Col, Container, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom';
import api from "../../../../services/api";
import ECCForm from './ECCForm';
import { ErrorNotification } from '../../../alerts';
import ConfirmGeneralAlert from '../../../alerts/ConfirmGeneralAlert';
import { useMsal } from '@azure/msal-react';
import { roles } from '../../../../auth/roles';
import { composeDataToSend, returnToApprovalList, validateApproval } from './ECCUtils';
import _ from 'lodash';

const apiUrl = "employeechangeconditionv2";

export default function ECCFormApproval() {
    const { id } = useParams();
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const eccId = query?.get("eccId");

    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [approvalData, setApprovalData] = useState(null);
    const [itemInEdit, setItemInEdit] = useState({});
    const [hrUser, setHrUser] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [validationObj, setValidationObj] = useState([]);
    const [showValidation, setShowValidation] = useState(false);
    const [showDeclineDialog, setShowDeclineDialog] = useState(false);
    const [declineReason, setDeclineReason] = useState(null);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const [canApprove, setCanApprove] = useState(false);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const history = useHistory();

    const isUserAdmin = user?.idTokenClaims.roles?.filter(r => r === roles.Admin).length > 0;

    const hasFullAccess = useMemo(() => {
        return _.intersection([roles.HR], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const hasAccess = useMemo(() => {
        return _.intersection([roles.HR, roles.Manager, roles.Admin], user.idTokenClaims.roles).length > 0;
    }, [roles, user]);

    const loadData = useCallback(() => {
        if (!id || !eccId)
            return;

        setLoading(true);
        setHrUser(null);
        const apiCalls = [
            api.get(`${apiUrl}/approval/${id}`),
            api.get(`${apiUrl}/${eccId}`),
            api.get(`userprofile/group?username=${user.username}&group=HR Director Team`),
        ]

        Promise.all(apiCalls)
            .then(responses => {
                if (responses[0]?.data)
                    setApprovalData({
                        ...responses[0].data
                    });

                if (responses[1]?.data)
                {
                    setItemInEdit({
                        ...responses[1].data
                    });
                    setDocuments(responses[1].data.eccDocuments ?? []);
                }

                if (responses[2]?.data)
                    setHrUser({...responses[2].data});
            })
            .catch((error) => console.error({error}))
            .finally(() => {
                setLoading(false);
            })
        ;
    }, [id, eccId, user]);

    useEffect(() => {
        if (!id || !eccId)
            return;

        loadData();
    }, [id, eccId]);

    useEffect(() => {
        setCanApprove(approvalData && approvalData.statusName === "Pending Approval" && (isUserAdmin || user?.username?.toLowerCase() === approvalData.approver?.email.toLowerCase() || hrUser));
    }, [approvalData, hrUser]);

    const scrollToValidation = useCallback(() => {
        if (!validationObj?.length)
            return;

        setTimeout(function () { //Start the timer
            const scrollObject = document.getElementById("validationAlert");
            if (!scrollObject) return;
            scrollObject.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }.bind(this), 500);
    }, [validationObj]);

    useEffect(() => {
        if (!showValidation)
            return;

        setShowValidation(false);
        scrollToValidation();
    }, [showValidation]);

    const handleSave = useCallback(() => {
        setLoading(true);
        const validationResult = validateApproval(itemInEdit, approvalData, documents);
        setValidationObj([...validationResult]);

        if (!eccId || validationResult?.length)
        {
            setShowValidation(true);
            setLoading(false);
            return;
        }
        
        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${apiUrl}/${eccId}`;
        const save = api({ url, method: "PUT", data: composeDataToSend(id, itemInEdit, documents) });
        
        save
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }

                returnToApprovalList(history);
            })
            .catch(error => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: `${error.response.data} Your data has not been saved.`,
                });
                setLoading(false);
                loadData();
            })
        ;
    }, [eccId, itemInEdit, history, hasFullAccess]);

    const handleSetApprovalStatus = useCallback((approved, reason) => {
        setLoading(true);
        const validationResult = validateApproval(itemInEdit, approvalData, documents);
        setValidationObj([...validationObj]);

        if (!id || validationResult?.length)
        {
            setShowValidation(true);
            setLoading(false);
            return;
        }

        const url = `${process.env.REACT_APP_WEB_API_BASE_URI}/${apiUrl}/status`;
        const save = api({ url, method: "POST", data: composeDataToSend(id, itemInEdit, documents, true, approved, reason) });
        
        save
            .then(() => {
                setLoading(false);

                if (!hasFullAccess)
                {
                    history.push("/");
                    return;
                }

                returnToApprovalList(history);
            })
            .catch(error => {
                console.error({error});
                setPopupMessageObj({
                    type: "error",
                    message: error.response.data.failedEmails ? "Fail to send the email." : error.response.data,
                });
                setLoading(false);
                loadData();
            })
        ;
    }, [id, eccId, itemInEdit, loadData, history, hasFullAccess]);

    const footer = useMemo(() => {
        return (
            <Row className="m-0 p-0 mt-3">
                <Col xs={12} className="d-flex justify-content-between m-0 p-0 mb-4">
                    <Row className="w-100 m-0 p-0">
                        <Col xs={3} className="d-flex flex-wrap justify-content-start align-items-center m-0 p-0">
                            <Button
                                color="info"
                                onClick={() => {
                                    if (!hasFullAccess)
                                    {
                                        history.push("/");
                                        return;
                                    }
                    
                                    returnToApprovalList(history);
                                }}
                                type="button"
                            >
                                Back
                            </Button>
                        </Col>
                    {
                        canApprove &&
                        <Col xs={9} className="d-flex flex-wrap justify-content-end align-items-center m-0 p-0">
                            <Button
                                color="default"
                                onClick={() => handleSave()}
                                type="button"
                            >
                                Save
                            </Button>
                            <Button
                                color="warning"
                                onClick={() => {
                                    setLoading(true);
                                    const validationResult = validateApproval(itemInEdit, approvalData, documents);
                                    setValidationObj([...validationObj]);

                                    if (validationResult?.length)
                                    {
                                        setShowValidation(true);
                                        setLoading(false);
                                        return;
                                    }

                                    setLoading(false);
                                    setShowDeclineDialog(true);
                                }}
                                type="button"
                            >
                                Reject
                            </Button>
                            <Button
                                color="default"
                                onClick={() => {
                                    setLoading(true);
                                    const validationResult = validateApproval(itemInEdit, approvalData, documents);
                                    setValidationObj([...validationObj]);

                                    if (validationResult?.length)
                                    {
                                        setShowValidation(true);
                                        setLoading(false);
                                        return;
                                    }

                                    setLoading(false);
                                    setPopupMessageObj({
                                        type: "confirmation",
                                        message: "Are you sure you want to approve the request?",
                                    });
                                }}
                                type="button"
                            >
                                Approve
                            </Button>
                        </Col>
                    }
                    </Row>
                </Col>
            </Row>
        );
    }, [itemInEdit, history, hasFullAccess]);

    if (!hasAccess)
        return (
            <Redirect
                to={{
                    pathname: '/unauthorized',
                    state: {
                        from: "eccform"
                    }
                }}
            />
        );

    return (
        <section className="main">
            {(loading) && <Loader />}
            <Container fluid>
                <ECCForm
                    isApproval={true}
                    itemInEdit={itemInEdit}
                    setItemInEdit={setItemInEdit}
                    documents={documents}
                    setDocuments={setDocuments}
                    setValidationObj={setValidationObj}
                    validationObj={validationObj}
                    loading={formLoading}
                    setLoading={setFormLoading}
                    footer={footer}
                />
            </Container>
        {
            showDeclineDialog &&
            <Modal
                isOpen={true}
                className="modal-lg"
                modalClassName="db-example-modal-lg"
            >
                <div
                    className="modal-header"
                >
                    <h5
                        className="modal-title"
                    >
                        ECC Request Rejection Dialog
                    </h5>
                    <button
                        aria-label="Close"
                        className="close"
                        onClick={() => setShowDeclineDialog(false)}
                        type="button"
                    >
                        <span aria-hidden={true}><i className="fas fa-times-circle" /></span>
                    </button>
                </div>
                <div
                    className="modal-body"
                >
                    <FormGroup>
                        <Label>
                            Reason
                            <span className="text-danger">*</span>
                        </Label>
                        <Input
                            className="w-100"
                            value={declineReason ?? ""}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            type="textarea"
                            rows={3}
                        />
                    </FormGroup>
                </div>
                <div
                    className="modal-footer"
                >
                    <Button
                        color="secondary"
                        onClick={() => setShowDeclineDialog(false)}
                        type="button"
                    >
                        Close
                    </Button>
                    <Button
                        color="warning"
                        onClick={() => handleSetApprovalStatus(false, declineReason)}
                        type="button"
                        disabled={!declineReason}
                    >
                        Reject
                    </Button>
                </div>
            </Modal>
        }
        {
            popupMessageObj?.type === "confirmation" ?
            <ConfirmGeneralAlert
                onConfirm={() => handleSetApprovalStatus(true)}
                onCancel={() => setPopupMessageObj(null)}
                textConfirm="Yes"
                title="ECC Approval Confirmation"
                textDesc={popupMessageObj.message}
            />
            :
            null
        }
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </section>
    );
};